import React, { useState, useEffect } from "react";
import { Mail, Phone, Edit3, Copy, Check } from "lucide-react";
import "../styles/06-pages/register.scss";

// Mock data für Unternehmen
const mockCompanies = [
  {
    id: "1",
    name: "SportCenter München GmbH",
    publicEmail: "info@sportcenter-muenchen.de",
  },
  {
    id: "2",
    name: "FitnessWorld Berlin",
    publicEmail: "kontakt@fitnessworld-berlin.de",
  },
  { id: "3", name: "Bowling Palace Hamburg", publicEmail: "" }, // Kein öffentliche E-Mail
  { id: "4", name: "AquaFun Köln", publicEmail: "service@aquafun-koeln.de" },
  {
    id: "5",
    name: "TennisClub Dresden",
    publicEmail: "verwaltung@tc-dresden.de",
  },
];

export default function Register() {
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companies] = useState(mockCompanies);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [email, setEmail] = useState("");
  const [originalEmail, setOriginalEmail] = useState("");
  const [isEmailReadonly, setIsEmailReadonly] = useState(false);
  const [emailManuallyEdited, setEmailManuallyEdited] = useState(false);
  const [acceptAGB, setAcceptAGB] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [copiedContact, setCopiedContact] = useState("");

  // Simulate loading companies
  useEffect(() => {
    const timer = setTimeout(() => {
      setCompaniesLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Handle company selection
  const handleCompanyChange = (companyId: string) => {
    setSelectedCompanyId(companyId);
    const company = companies.find((c) => c.id === companyId);

    if (company?.publicEmail) {
      setEmail(company.publicEmail);
      setOriginalEmail(company.publicEmail);
      setIsEmailReadonly(true);
      setEmailManuallyEdited(false);
    } else {
      setEmail("");
      setOriginalEmail("");
      setIsEmailReadonly(false);
      setEmailManuallyEdited(false);
    }
  };

  // Handle email edit
  const handleEmailEdit = () => {
    setIsEmailReadonly(false);
    setEmailManuallyEdited(true);
  };

  // Validation
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isFormValid =
    selectedCompanyId && email && isEmailValid && acceptAGB && acceptPrivacy;

  // Submit verification
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock function - replace with real API call
      await sendVerificationEmail(selectedCompanyId, email);

      showToast(
        "Verifizierungs-E-Mail gesendet",
        "Bitte prüfen Sie Ihr Postfach (inkl. Spam)."
      );
    } catch (error) {
      showToast(
        "Fehler",
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
        "destructive"
      );
    } finally {
      setLoading(false);
    }
  };

  // Mock function for sending verification email
  const sendVerificationEmail = async (companyId: string, email: string) => {
    console.log(
      "Sending verification email to:",
      email,
      "for company:",
      companyId
    );
    // This would be replaced with actual API call
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedContact(type);
    setTimeout(() => setCopiedContact(""), 2000);
    // Simple alert instead of toast for now
    alert(`${type} wurde kopiert: ${text}`);
  };

  // Simple toast-like function
  const showToast = (title: string, description: string, variant?: string) => {
    if (variant === "destructive") {
      alert(`Fehler: ${description}`);
    } else {
      alert(`${title}: ${description}`);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-card">
          {/* Überschrift */}
          <h1 className="register-title">Registrieren als</h1>

          <form onSubmit={handleSubmit}>
            {/* Unternehmen Dropdown */}
            <div className="form-group">
              <label htmlFor="company" className="form-label">
                Unternehmen auswählen*
              </label>
              {companiesLoading ? (
                <div className="skeleton"></div>
              ) : (
                <select
                  id="company"
                  value={selectedCompanyId}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  className="form-select"
                >
                  <option value="">Bitte wählen...</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Hinweistext über E-Mail-Feld */}
            {selectedCompanyId && originalEmail && (
              <div className="info-box">
                <p className="info-text">
                  Wir haben diese E-Mail-Adresse öffentlich bei Ihnen gefunden.
                </p>
                <p className="info-text">
                  Wir senden Ihnen eine E-Mail, um sich bei uns zu verifizieren.
                  Führen Sie dann die weiteren Schritte durch.
                </p>
              </div>
            )}

            {/* E-Mail-Feld */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Geschäftliche E-Mail-Adresse*
              </label>
              <div className="email-container">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@unternehmen.de"
                  readOnly={isEmailReadonly}
                  className="form-input"
                />
                {isEmailReadonly && (
                  <button
                    type="button"
                    onClick={handleEmailEdit}
                    className="edit-button"
                  >
                    <Edit3 className="icon" />
                  </button>
                )}
              </div>
              {emailManuallyEdited && (
                <p className="email-edited-text">
                  E-Mail-Adresse wurde manuell angepasst.
                </p>
              )}
            </div>

            {/* AGB & Datenschutz */}
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="agb"
                  checked={acceptAGB}
                  onChange={(e) => setAcceptAGB(e.target.checked)}
                  className="checkbox-input"
                />
                <label htmlFor="agb" className="checkbox-label">
                  Ich habe die{" "}
                  <a href="/agb" className="checkbox-link">
                    AGB
                  </a>{" "}
                  gelesen und stimme zu.
                </label>
              </div>

              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  className="checkbox-input"
                />
                <label htmlFor="privacy" className="checkbox-label">
                  Ich habe die{" "}
                  <a href="/datenschutz" className="checkbox-link">
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und stimme zu.
                </label>
              </div>
            </div>

            {/* Verifizieren Button */}
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="submit-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  <span>Wird gesendet...</span>
                </>
              ) : (
                "Verifizieren"
              )}
            </button>
          </form>

          {/* Alternative Kontaktmöglichkeit */}
          <div className="contact-section">
            <h3 className="contact-title">
              Sie möchten sich direkt bei uns melden und verifizieren?
            </h3>

            <button
              onClick={() =>
                (window.location.href =
                  "mailto:support@offtogether.de?subject=Direkte Verifizierung")
              }
              className="contact-button"
            >
              <Mail className="icon" />
              Jetzt direkt melden
            </button>

            {/* Kontaktangaben */}
            <div className="contact-info">
              <div className="contact-item">
                <Mail className="icon" />
                <span>support@offtogether.de</span>
                <button
                  onClick={() =>
                    copyToClipboard("support@offtogether.de", "E-Mail")
                  }
                  className={`copy-button ${
                    copiedContact === "E-Mail" ? "copied" : ""
                  }`}
                >
                  {copiedContact === "E-Mail" ? (
                    <Check className="icon-sm" />
                  ) : (
                    <Copy className="icon-sm" />
                  )}
                </button>
              </div>

              <div className="contact-item">
                <Phone className="icon" />
                <span>+49 30 12345678</span>
                <button
                  onClick={() => copyToClipboard("+49 30 12345678", "Telefon")}
                  className={`copy-button ${
                    copiedContact === "Telefon" ? "copied" : ""
                  }`}
                >
                  {copiedContact === "Telefon" ? (
                    <Check className="icon-sm" />
                  ) : (
                    <Copy className="icon-sm" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
